import React, { useContext, useMemo, useState } from 'react'
import { ChartView, ChartViews } from '../utils'
import moment from 'moment'
import { getMaxFromDateValue, getMaxToDateValue, getToDateValue } from 'src/Libs/system-data-handler/utils/datetime'
import { ChartsDateBarContext } from './chart-date-bar-context'
import { ChartDateBarContextType } from '../types'

interface Props {
  children: React.ReactElement
}

/**
 * ChartDateBarProvider is a context provider with state to configure chart date settings and state to handle displaying the chart date bar.
 * Can be used if multiple charts share one date bar.
 */
export const ChartDateBarProvider: React.FC<Props> = ({ children }) => {
  const [view, setView] = useState<ChartView>(ChartViews.Day)
  const [hasCustomRange, setHasCustomRange] = useState(false)
  const [from, setFrom] = useState(moment().startOf(view))
  const [to, setTo] = useState(getToDateValue(from, view, hasCustomRange))
  const [maxTo, setMaxTo] = useState(getMaxToDateValue(view))
  const [isForecastEnabled, setIsForecastEnabled] = useState(false)
  const maxFrom = useMemo(() => getMaxFromDateValue(view, hasCustomRange, from), [view, hasCustomRange, from])
  const [isChartDateBarSticky, setIsChartDateBarSticky] = useState(false)
  const [areSystemChartsVisible, setAreSystemChartsVisible] = useState(true)
  const [isCostSavingChartVisible, setIsCostSavingChartVisible] = useState(true)
  const resolution = useMemo(() => {
    if (hasCustomRange) {
      const shouldUseHourResolution = moment(to).diff(moment(from), 'days') < 3 && view === ChartViews.Day
      const shouldUseDayResolution = moment(to).diff(moment(from), 'days') < 32
      if (shouldUseHourResolution) return ChartViews.Day
      if (shouldUseDayResolution) return ChartViews.Week
      return ChartViews.Year
    }
    return view
  }, [hasCustomRange, view, from, to])

  const value = useMemo(() => {
    return {
      from,
      setFrom,
      maxFrom,
      to,
      setTo,
      maxTo,
      setMaxTo,
      view,
      setView,
      resolution,
      isForecastEnabled,
      setIsForecastEnabled,
      hasCustomRange,
      setHasCustomRange,
      isChartDateBarSticky,
      setIsChartDateBarSticky,
      areSystemChartsVisible,
      setAreSystemChartsVisible,
      isCostSavingChartVisible,
      setIsCostSavingChartVisible,
    }
  }, [
    from,
    maxFrom,
    to,
    maxTo,
    view,
    resolution,
    isForecastEnabled,
    hasCustomRange,
    isChartDateBarSticky,
    areSystemChartsVisible,
    isCostSavingChartVisible,
  ])

  return <ChartsDateBarContext.Provider value={value}>{children}</ChartsDateBarContext.Provider>
}

export const useChartDateBarContext = (): ChartDateBarContextType => {
  const context = useContext(ChartsDateBarContext)
  if (!context) {
    throw new Error('useChartDateBarContext must be used within a ChartDateBarProvider')
  }
  return context
}
